import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import SprintTable from "../../sprint/components/SprintTable";
import usePagination from "../../../hooks/usePagination";
import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall.js";
import useSearchTerm from "../../../hooks/useSearchTerm.js";
import useDelayedLoading from "../../../hooks/useDelayedLoading";
import useDebounce from "../../../hooks/useDebounce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlag } from "@fortawesome/free-solid-svg-icons";
import Loading from "../../layout/Loading.jsx";
import Button from "../../core/Button.jsx";
import SearchInput from "../../input/SearchInput.jsx";
import SecurityWrapper from "../../auth/SecurityWrapper.jsx";

const SprintListPage = () => {
  const [searchTerm, setSearchTerm] = useSearchTerm();
  const [filterSprint, setFilterSprint] = useState("active");
  const [loading, setLoading] = useState(true);
  const history = useHistory();

  const [sortParams, setSortParams] = useState(() => {
    const queryParams = new URLSearchParams(history.location.search);

    return {
      sortBy: queryParams.get("sort") || "",
      sortDir: queryParams.get("sort_dir") || "asc",
    };
  });
  const searchDebounce = useDebounce(searchTerm);

  const endpoint = useMemo(() => {
    const params = new URLSearchParams("");
    params.set("sort", sortParams.sortBy);
    params.set("sort_dir", sortParams.sortDir);
    if (searchDebounce) params.set("q", searchDebounce);

    let base;
    if (filterSprint === "all") base = "/sprints";
    else if (filterSprint === "active") base = "/sprint/active";
    else base = "/sprint/inactive";

    return `${base}?${params.toString()}`;
  }, [filterSprint, sortParams, searchDebounce]);

  const { fetchData, data } = useTriggeredAPICall();
  const {
    currentData: sprints,
    recordsData,
    listPage,
  } = usePagination(data, endpoint, fetchData);

  const showLoading = useDelayedLoading(loading);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await listPage(1);
      setLoading(false);
    };
    load();
  }, [endpoint]);

  const enrichedSprints = useMemo(() => {
    return sprints?.map((sprint) => {
      const storedProgress = localStorage.getItem(
        `sprint_progress_${sprint.sprint_id}`
      );

      let progress = 0;
      if (storedProgress) {
        try {
          const progressData = JSON.parse(storedProgress);
          const oneHour = 60 * 60 * 1000;
          if (Date.now() - progressData.lastUpdated < oneHour) {
            progress = progressData.progress;
          }
        } catch {}
      }

      return {
        ...sprint,
        progress,
        is_active: new Date(sprint.end_date) >= new Date(),
      };
    });
  }, [sprints]);

  const handlePageChange = (page) => {
    setLoading(true);
    listPage(page).finally(() => setLoading(false));
  };

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);

    if (sortParams.sortBy) {
      params.set("sort", sortParams.sortBy);
      params.set("sort_dir", sortParams.sortDir);
    } else {
      params.delete("sort");
      params.delete("sort_dir");
    }

    history.replace({
      pathname: history.location.pathname,
      search: params.toString(),
    });
  }, [sortParams, history]);

  return (
    <div className="flex-align-start width-full">
      <h1>
        <FontAwesomeIcon icon={faFlag} className="sprints-icon" /> Sprints
      </h1>

      <div className="flex-space-between">
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="width-200px"
            placeholder="Search sprints..."
          />

          <label>
            <input
              type="checkbox"
              checked={filterSprint === "all"}
              onChange={(e) => {
                setFilterSprint("all");
              }}
            />
            <span className="ml-2">All</span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={filterSprint === "active"}
              onChange={(e) => {
                setFilterSprint("active");
              }}
            />
            <span className="ml-2">Show Active</span>
          </label>

          <label>
            <input
              type="checkbox"
              checked={filterSprint === "in-active"}
              onChange={(e) => {
                setFilterSprint("in-active");
              }}
            />
            <span className="ml-2">Show Inactive</span>
          </label>
        </div>

        <SecurityWrapper requiredRoles={["admin"]}>
          <Button to="/sprint-form">+ New Sprint</Button>
        </SecurityWrapper>
      </div>

      <SprintTable
        data={enrichedSprints}
        loading={showLoading}
        recordsData={recordsData}
        onPageChange={handlePageChange}
        baseUrl={endpoint}
        setSortParams={setSortParams}
        sortParams={sortParams}
      />
    </div>
  );
};

export default SprintListPage;
